import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { FC, PropsWithChildren } from "react";
import { useCallback, useEffect, useRef } from "react";

import type { SideBarVariantProps } from "~/components/Sidebar/sidebarVariants.ts";
import { sideBarVariants } from "~/components/Sidebar/sidebarVariants.ts";
import useOnClickOutside from "~/hooks/useOnClickOutside.ts";

type SideBarProps = PropsWithChildren & SideBarVariantProps & {
	open: boolean;
	setOpen: (bool: boolean) => void;
	closeOnOutsideClick?: boolean;
	closeOnEscape?: boolean;
}

const Sidebar: FC<SideBarProps> = ({
	children,
	open,
	setOpen,
	theme,
	width,
	closeOnEscape = false,
	closeOnOutsideClick = true
}) => {
	const sidebarRef = useRef<HTMLDivElement>(null);
	const outsideClickHandler = useCallback(() => {
		if (closeOnOutsideClick) {
			setOpen(false);
		}
	}, [closeOnOutsideClick, setOpen]);

	useOnClickOutside(sidebarRef, () => setOpen(false), closeOnOutsideClick);

	const {innerWrapper, contentContainer} = sideBarVariants({width, theme})

	useEffect(() => {
		const keyHandler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setOpen(false);
			}
		}
		if (closeOnEscape && open) {
			document.addEventListener("keydown", keyHandler);
		} else {
			document.removeEventListener("keydown", keyHandler);
		}
		return () => document.removeEventListener("keydown", keyHandler);
	}, [open, closeOnEscape, setOpen]);

	return (
		<Dialog open={open}
				onClose={outsideClickHandler}
				className="relative z-50">

			<div className="fixed inset-0 overflow-hidden">
				<div className="absolute inset-0 overflow-hidden">
					<DialogBackdrop
						transition
						className="fixed inset-0 cursor-pointer bg-gray-500/75 duration-300 ease-out data-[closed]:opacity-0"
					/>
					<div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">

						<DialogPanel
							transition
							className={clsx(innerWrapper(), "duration-300 ease-in-out data-[closed]:translate-x-full")}
						>
							<div className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] z-10 flex sm:inset-auto sm:left-0 sm:top-0 sm:-ml-10 sm:pr-4 sm:pt-4">
								<button
									type="button"
									className="rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 sm:text-gray-300 sm:hover:text-white sm:focus:ring-white"
									onClick={() => setOpen(false)}
								>
									<span className="sr-only">Close panel</span>
									<XMarkIcon className="size-6"
											   aria-hidden="true"/>
								</button>
							</div>
							<div className={contentContainer()}>
								{children}
							</div>
						</DialogPanel>

					</div>
				</div>
			</div>
		</Dialog>
	)
}

export default Sidebar;
